import { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

// ============================================================================
// MOCK DATA
// ============================================================================

interface MockProp {
  id: string;
  description: string;
  costCoins: number;
  bonusMultiplier: number;
  category: string;
  line: number | null;
  gameId: string;
  side?: 'OVER' | 'UNDER';
}

interface MockMyProp {
  undercardPropId: string;
  result: 'PENDING' | 'HIT' | 'MISS' | null;
  bonusPoints: number;
}

interface MockGame {
  id: string;
  homeTeam: string;
  awayTeam: string;
  homeSeed: number | null;
  awaySeed: number | null;
}

const CATEGORY_LABELS: Record<string, string> = {
  PLAYER_POINTS: 'Points',
  PLAYER_REBOUNDS: 'Rebounds',
  PLAYER_ASSISTS: 'Assists',
  PLAYER_POINTS_REBOUNDS: 'Pts+Reb',
  PLAYER_POINTS_ASSISTS: 'Pts+Ast',
  PLAYER_REBOUNDS_ASSISTS: 'Reb+Ast',
};

const MOCK_GAMES: MockGame[] = [
  { id: 'game-1', homeTeam: 'UConn Huskies', awayTeam: 'Michigan St Spartans', homeSeed: 1, awaySeed: 9 },
  { id: 'game-2', homeTeam: 'Duke Blue Devils', awayTeam: 'Arkansas Razorbacks', homeSeed: 2, awaySeed: 4 },
];

/**
 * Generate player props with separate Over/Under entries and directional bonuses.
 * Over: bonus increases as line goes UP from primary (harder to hit over a higher number).
 * Under: bonus increases as line goes DOWN from primary (harder to hit under a lower number).
 * Primary line is in the middle of the lines array.
 *
 * Set `mainOnly: true` to generate a single primary line with no alts (for testing main-line-only UI).
 */
function makePlayerProps(gameId: string, playerName: string, cats: { cat: string; lines: number[]; mainOnly?: boolean }[]): MockProp[] {
  const props: MockProp[] = [];
  for (const { cat, lines, mainOnly } of cats) {
    const primaryIdx = Math.floor(lines.length / 2);
    const primaryLine = lines[primaryIdx];
    const linesToUse = mainOnly ? [primaryLine] : lines;

    for (const line of linesToUse) {
      const off = line - primaryLine;

      // Over: going UP = harder = more bonus; going DOWN = easier = less bonus
      const overMult = off >= 0
        ? Math.round((1.0 + off * 0.15) * 100) / 100
        : Math.round(Math.max(0.4, 1.0 + off * 0.06) * 100) / 100;

      // Under: going DOWN = harder = more bonus; going UP = easier = less bonus
      const underMult = off <= 0
        ? Math.round((1.0 + Math.abs(off) * 0.15) * 100) / 100
        : Math.round(Math.max(0.4, 1.0 - off * 0.06) * 100) / 100;

      props.push({
        id: `${gameId}-${playerName}-${cat}-over-${line}`,
        description: `${playerName} \u2014 ${CATEGORY_LABELS[cat] || cat} Over ${line}`,
        costCoins: Math.max(15, Math.round(50 * overMult / 5) * 5),
        bonusMultiplier: overMult,
        category: cat,
        line,
        gameId,
        side: 'OVER' as const,
      });
      props.push({
        id: `${gameId}-${playerName}-${cat}-under-${line}`,
        description: `${playerName} \u2014 ${CATEGORY_LABELS[cat] || cat} Under ${line}`,
        costCoins: Math.max(15, Math.round(50 * underMult / 5) * 5),
        bonusMultiplier: underMult,
        category: cat,
        line,
        gameId,
        side: 'UNDER' as const,
      });
    }
  }
  return props;
}

const MOCK_PROPS: MockProp[] = [
  // UConn players
  ...makePlayerProps('game-1', 'Donovan Clingan', [
    { cat: 'PLAYER_POINTS', lines: [12.5, 14.5, 16.5, 18.5, 20.5] },
    { cat: 'PLAYER_REBOUNDS', lines: [6.5, 8.5, 10.5, 12.5] },
    { cat: 'PLAYER_ASSISTS', lines: [1.5, 2.5, 3.5] },
  ]),
  ...makePlayerProps('game-1', 'Tristen Newton', [
    { cat: 'PLAYER_POINTS', lines: [10.5, 12.5, 14.5, 16.5] },
    { cat: 'PLAYER_ASSISTS', lines: [3.5, 4.5, 5.5, 6.5] },
  ]),
  ...makePlayerProps('game-1', 'Stephon Castle', [
    { cat: 'PLAYER_POINTS', lines: [8.5, 10.5, 12.5, 14.5] },
    { cat: 'PLAYER_REBOUNDS', lines: [3.5], mainOnly: true }, // main line only — no alts
    { cat: 'PLAYER_POINTS_REBOUNDS', lines: [14.5], mainOnly: true }, // main line only
  ]),
  // MSU players
  ...makePlayerProps('game-1', 'Tyson Walker', [
    { cat: 'PLAYER_POINTS', lines: [12.5, 14.5, 16.5, 18.5] },
    { cat: 'PLAYER_ASSISTS', lines: [2.5, 3.5, 4.5] },
  ]),
  ...makePlayerProps('game-1', 'Malik Hall', [
    { cat: 'PLAYER_POINTS', lines: [8.5, 10.5, 12.5] },
    { cat: 'PLAYER_REBOUNDS', lines: [4.5, 5.5, 6.5, 7.5] },
  ]),
  // Duke players
  ...makePlayerProps('game-2', 'Kyle Filipowski', [
    { cat: 'PLAYER_POINTS', lines: [14.5, 16.5, 18.5, 20.5, 22.5] },
    { cat: 'PLAYER_REBOUNDS', lines: [6.5, 8.5, 10.5] },
    { cat: 'PLAYER_ASSISTS', lines: [1.5, 2.5, 3.5] },
  ]),
  ...makePlayerProps('game-2', 'Jared McCain', [
    { cat: 'PLAYER_POINTS', lines: [10.5, 12.5, 14.5, 16.5] },
    { cat: 'PLAYER_ASSISTS', lines: [2.5, 3.5, 4.5] },
  ]),
];

// Pre-purchased props for demo
const MOCK_MY_PROPS: MockMyProp[] = [
  { undercardPropId: 'game-2-Kyle Filipowski-PLAYER_POINTS-over-18.5', result: 'HIT', bonusPoints: 3.5 },
];

function getAbbrev(teamName: string): string {
  const map: Record<string, string> = {
    'UConn Huskies': 'UCONN', 'Michigan St Spartans': 'MSU',
    'Duke Blue Devils': 'DUKE', 'Arkansas Razorbacks': 'ARK',
  };
  return map[teamName] || teamName.split(' ')[0].slice(0, 4).toUpperCase();
}

function extractPlayerName(description: string): string {
  const dashIdx = description.indexOf(' \u2014 ');
  if (dashIdx > 0) return description.substring(0, dashIdx);
  return 'Unknown';
}

function formatLine(line: number | null): string {
  if (line === null) return '\u2014';
  return line > 0 ? `+${line}` : `${line}`;
}

function fmtPts(n: number): string {
  return parseFloat(n.toFixed(3)).toString();
}

// ============================================================================
// PLAYER PROP COMPONENTS (redesigned)
// ============================================================================

/** Single category — O/U toggle + slider (or main-line-only simplified buy) */
function PlayerCategoryRow({ category, props, myProps, purchasedPropIds, onPurchase, disabled }: {
  category: string;
  props: MockProp[];
  myProps: MockMyProp[];
  purchasedPropIds: Set<string>;
  onPurchase: (propId: string) => void;
  disabled: boolean;
}) {
  const [side, setSide] = useState<'OVER' | 'UNDER'>('OVER');
  const [sliderIdx, setSliderIdx] = useState(-1);
  const [confirmPropId, setConfirmPropId] = useState<string | null>(null);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleBuyClick = useCallback((propId: string) => {
    if (confirmPropId === propId) {
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
      setConfirmPropId(null);
      onPurchase(propId);
    } else {
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
      setConfirmPropId(propId);
      confirmTimerRef.current = setTimeout(() => setConfirmPropId(null), 3000);
    }
  }, [confirmPropId, onPurchase]);

  useEffect(() => {
    setConfirmPropId(null);
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
  }, [sliderIdx, side]);
  useEffect(() => {
    return () => { if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current); };
  }, []);

  // Filter by selected side
  const sideProps = props.filter((p) => p.side === side);
  const sorted = [...sideProps].sort((a, b) => (a.line ?? 0) - (b.line ?? 0));
  const displayProps = sorted.filter((p) => p.line !== null);
  const uniqueLines = new Set(displayProps.map((p) => p.line));
  const isMainLineOnly = uniqueLines.size <= 1;
  const hasAltLines = uniqueLines.size >= 2;

  const effectiveIndex = sliderIdx >= 0 && sliderIdx < displayProps.length
    ? sliderIdx
    : displayProps.reduce((bestIdx, p, i, arr) => {
        const bestDist = Math.abs((arr[bestIdx]?.bonusMultiplier ?? 99) - 1.0);
        const curDist = Math.abs(p.bonusMultiplier - 1.0);
        return curDist < bestDist ? i : bestIdx;
      }, 0);

  const currentProp = displayProps[effectiveIndex] || displayProps[0];
  const hasPurchased = props.some((p) => purchasedPropIds.has(p.id));
  const purchasedProp = hasPurchased ? props.find((p) => purchasedPropIds.has(p.id)) : null;
  const purchasedData = purchasedProp ? myProps.find((m) => m.undercardPropId === purchasedProp.id) ?? null : null;
  const isResolved = purchasedData?.result === 'HIT' || purchasedData?.result === 'MISS';
  const label = CATEGORY_LABELS[category] || category;

  if (!currentProp && !purchasedProp) return null;

  // Purchased state — compact inline
  if (hasPurchased && purchasedProp) {
    const purchasedSide = purchasedProp.side === 'OVER' ? 'Over' : purchasedProp.side === 'UNDER' ? 'Under' : '';
    return (
      <div className="flex items-center gap-2 text-xs py-1.5">
        <span className="text-warm-gray/60 text-[10px] font-medium">{label}</span>
        {purchasedSide && <span className="text-warm-gray/50 text-[10px]">{purchasedSide}</span>}
        <span className="font-bold text-cream">{purchasedProp.line}</span>
        <span className="text-warm-gray/50">{purchasedProp.bonusMultiplier}x</span>
        <span className={cn(
          'ml-auto px-2 py-0.5 rounded text-[10px] font-bold',
          isResolved
            ? purchasedData?.result === 'HIT' ? 'bg-verdigris/20 text-verdigris' : 'bg-oxblood/20 text-oxblood'
            : 'bg-gold/10 text-gold',
        )}>
          {isResolved
            ? (purchasedData?.result === 'HIT' ? `HIT +${fmtPts(purchasedData?.bonusPoints ?? 0)}` : 'MISS')
            : 'Purchased'}
        </span>
      </div>
    );
  }

  // Locked
  if (disabled) {
    return (
      <div className="flex items-center gap-2 text-[10px] text-warm-gray/40 py-1">
        <span className="font-medium">{label}</span>
        <span>O/U {currentProp.line}</span>
        <span>{currentProp.bonusMultiplier}x</span>
      </div>
    );
  }

  // Main line only — simplified: O/U toggle + direct buy, no slider
  if (isMainLineOnly) {
    return (
      <div className="py-1.5 space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-warm-gray/60 font-medium">{label}</span>
          <span className="text-[10px] text-warm-gray/30 italic">main line</span>
        </div>
        <div className="flex items-center gap-1.5">
          {/* O/U mini toggle */}
          <button
            onClick={() => setSide('OVER')}
            className={cn(
              'px-2 py-0.5 rounded text-[10px] font-bold transition-all',
              side === 'OVER'
                ? 'bg-gold/20 border border-gold text-gold'
                : 'bg-surface-3 border border-transparent text-warm-gray hover:border-surface-4',
            )}
          >O</button>
          <button
            onClick={() => setSide('UNDER')}
            className={cn(
              'px-2 py-0.5 rounded text-[10px] font-bold transition-all',
              side === 'UNDER'
                ? 'bg-gold/20 border border-gold text-gold'
                : 'bg-surface-3 border border-transparent text-warm-gray hover:border-surface-4',
            )}
          >U</button>
          <span className="text-xs font-bold text-cream">{currentProp.line}</span>
          <span className="text-[10px] text-warm-gray/50">{currentProp.bonusMultiplier}x</span>
          <span className="flex-1" />
          {confirmPropId === currentProp.id ? (
            <button
              onClick={() => handleBuyClick(currentProp.id)}
              className="px-2 py-0.5 rounded text-[10px] font-semibold bg-cream/90 text-[#3a2a1a] hover:bg-cream transition-colors whitespace-nowrap"
            >
              Confirm {currentProp.costCoins}c
            </button>
          ) : (
            <button
              onClick={() => handleBuyClick(currentProp.id)}
              className="px-2 py-0.5 rounded text-[10px] font-semibold bg-gold/20 text-gold hover:bg-gold/30 transition-colors whitespace-nowrap border border-gold/30"
            >
              {currentProp.costCoins}c
            </button>
          )}
        </div>
      </div>
    );
  }

  // Alt lines available — O/U toggle + slider
  return (
    <div className="py-1.5 space-y-1.5">
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-warm-gray/60 font-medium">{label}</span>
      </div>
      {/* O/U toggle */}
      <div className="flex gap-1">
        <button
          onClick={() => { setSide('OVER'); setSliderIdx(-1); }}
          className={cn(
            'flex-1 py-0.5 px-2 rounded text-[10px] font-bold transition-all',
            side === 'OVER'
              ? 'bg-gold/20 border border-gold text-gold'
              : 'bg-surface-3 border border-transparent text-warm-gray hover:border-surface-4',
          )}
        >Over</button>
        <button
          onClick={() => { setSide('UNDER'); setSliderIdx(-1); }}
          className={cn(
            'flex-1 py-0.5 px-2 rounded text-[10px] font-bold transition-all',
            side === 'UNDER'
              ? 'bg-gold/20 border border-gold text-gold'
              : 'bg-surface-3 border border-transparent text-warm-gray hover:border-surface-4',
          )}
        >Under</button>
      </div>
      {/* Line + slider + buy */}
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold text-cream whitespace-nowrap">{currentProp.line ?? '\u2014'}</span>

        {hasAltLines && (
          <input
            type="range"
            min={0}
            max={displayProps.length - 1}
            value={Math.min(effectiveIndex, displayProps.length - 1)}
            onChange={(e) => setSliderIdx(Number(e.target.value))}
            className="flex-1 h-1 rounded-full appearance-none cursor-pointer min-w-[40px]
              bg-gradient-to-r from-verdigris/40 via-gold/40 to-oxblood/40
              [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3
              [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-gold [&::-webkit-slider-thumb]:shadow-lg
              [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-cream
              [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3
              [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-gold [&::-moz-range-thumb]:shadow-lg
              [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-cream [&::-moz-range-thumb]:border-0"
          />
        )}

        <span className="text-[10px] text-warm-gray/50 whitespace-nowrap">{currentProp.bonusMultiplier}x</span>

        {confirmPropId === currentProp.id ? (
          <button
            onClick={() => handleBuyClick(currentProp.id)}
            className="px-2 py-0.5 rounded text-[10px] font-semibold bg-cream/90 text-[#3a2a1a] hover:bg-cream transition-colors whitespace-nowrap"
          >
            Confirm {currentProp.costCoins}c
          </button>
        ) : (
          <button
            onClick={() => handleBuyClick(currentProp.id)}
            className="px-2 py-0.5 rounded text-[10px] font-semibold bg-gold/20 text-gold hover:bg-gold/30 transition-colors whitespace-nowrap border border-gold/30"
          >
            {currentProp.costCoins}c
          </button>
        )}
      </div>
    </div>
  );
}

/** Collapsible player card — name header, category rows inside */
function PlayerPropCard({ playerName, props, myProps, purchasedPropIds, onPurchase, disabled }: {
  playerName: string;
  props: MockProp[];
  myProps: MockMyProp[];
  purchasedPropIds: Set<string>;
  onPurchase: (propId: string) => void;
  disabled: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  const purchasedCount = props.filter((p) => purchasedPropIds.has(p.id)).length;

  // Group by category
  const byCategory = new Map<string, MockProp[]>();
  for (const prop of props) {
    const cat = prop.category || 'OTHER';
    const arr = byCategory.get(cat) || [];
    arr.push(prop);
    byCategory.set(cat, arr);
  }

  const categoryOrder = [
    'PLAYER_POINTS', 'PLAYER_REBOUNDS', 'PLAYER_ASSISTS',
    'PLAYER_POINTS_REBOUNDS', 'PLAYER_POINTS_ASSISTS', 'PLAYER_REBOUNDS_ASSISTS',
    'OTHER',
  ];
  const sortedCategories = [...byCategory.entries()].sort(
    (a, b) => {
      const ai = categoryOrder.indexOf(a[0]);
      const bi = categoryOrder.indexOf(b[0]);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    }
  );

  // "Last, First" format
  const parts = playerName.split(' ');
  const displayName = parts.length >= 2
    ? `${parts.slice(-1)[0]}, ${parts.slice(0, -1).join(' ')}`
    : playerName;

  // Primary line preview (goals line if available)
  const pointsProp = props.find((p) => p.category === 'PLAYER_POINTS' && p.side === 'OVER');
  const previewLine = pointsProp ? `${pointsProp.line} goals` : `${sortedCategories.length} cats`;

  // Count how many categories are main-line-only (no alt lines)
  const mainOnlyCount = sortedCategories.filter(([, catProps]) => {
    const overProps = catProps.filter((p) => p.side === 'OVER');
    return new Set(overProps.map((p) => p.line)).size <= 1;
  }).length;

  return (
    <div className="border-b border-surface-3/50 last:border-b-0">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-surface-3/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <ChevronDown className={cn(
            'w-3 h-3 text-warm-gray/40 transition-transform',
            !expanded && '-rotate-90',
          )} />
          <span className="text-cream text-xs font-medium">{displayName}</span>
          <span className="text-[10px] text-warm-gray/40">{previewLine}</span>
        </div>
        <div className="flex items-center gap-2">
          {purchasedCount > 0 && (
            <span className="text-[10px] text-gold font-medium">{purchasedCount} bought</span>
          )}
          {mainOnlyCount > 0 && purchasedCount === 0 && (
            <span className="text-[9px] text-warm-gray/30 italic">{mainOnlyCount} main only</span>
          )}
          <span className="text-[10px] text-warm-gray/30">{sortedCategories.length}</span>
        </div>
      </button>

      {expanded && (
        <div className="px-3 pb-2 space-y-0.5">
          {sortedCategories.map(([category, catProps]) => (
            <PlayerCategoryRow
              key={category}
              category={category}
              props={catProps}
              myProps={myProps}
              purchasedPropIds={purchasedPropIds}
              onPurchase={onPurchase}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Game-level player props card — game header + collapsible player list */
function GamePlayerPropCard({ game, props, myProps, purchasedPropIds, onPurchase, disabled }: {
  game: MockGame;
  props: MockProp[];
  myProps: MockMyProp[];
  purchasedPropIds: Set<string>;
  onPurchase: (propId: string) => void;
  disabled: boolean;
}) {
  const [expanded, setExpanded] = useState(true);

  // Group by player
  const byPlayer = new Map<string, MockProp[]>();
  for (const prop of props) {
    const name = extractPlayerName(prop.description);
    const arr = byPlayer.get(name) || [];
    arr.push(prop);
    byPlayer.set(name, arr);
  }

  // Sort: highest goals line first
  const sortedPlayers = [...byPlayer.entries()].sort((a, b) => {
    const aLine = a[1].find((p) => p.category === 'PLAYER_POINTS')?.line ?? 0;
    const bLine = b[1].find((p) => p.category === 'PLAYER_POINTS')?.line ?? 0;
    return bLine - aLine;
  });

  const purchasedInGame = props.filter((p) => purchasedPropIds.has(p.id)).length;

  return (
    <div className="rounded-xl border border-surface-3 bg-surface-2 overflow-hidden max-w-sm w-full">
      {/* Game header bar — matches UnifiedGameCard style */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-2 bg-surface-3/50 border-b border-surface-3 hover:bg-surface-3/70 transition-colors"
      >
        <div className="flex items-center gap-2 text-[11px] font-bold text-cream">
          <span>
            {game.awaySeed && <span className="text-warm-gray/50 mr-0.5">({game.awaySeed})</span>}
            {getAbbrev(game.awayTeam)}
          </span>
          <span className="text-warm-gray/40 text-[10px]">@</span>
          <span>
            {game.homeSeed && <span className="text-warm-gray/50 mr-0.5">({game.homeSeed})</span>}
            {getAbbrev(game.homeTeam)}
          </span>
          <span className="text-[10px] text-warm-gray/40 font-normal ml-1">{sortedPlayers.length} players</span>
        </div>
        <div className="flex items-center gap-2">
          {purchasedInGame > 0 && (
            <span className="text-[10px] text-gold font-medium">{purchasedInGame} bought</span>
          )}
          <ChevronDown className={cn(
            'w-3.5 h-3.5 text-warm-gray/40 transition-transform',
            !expanded && '-rotate-90',
          )} />
        </div>
      </button>

      {expanded && (
        <div>
          {sortedPlayers.map(([playerName, playerProps]) => (
            <PlayerPropCard
              key={playerName}
              playerName={playerName}
              props={playerProps}
              myProps={myProps}
              purchasedPropIds={purchasedPropIds}
              onPurchase={onPurchase}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// PREVIEW PAGE
// ============================================================================

export default function PlayerPropsPreview() {
  const [purchased, setPurchased] = useState<Set<string>>(
    new Set(MOCK_MY_PROPS.map((m) => m.undercardPropId))
  );

  const handlePurchase = (propId: string) => {
    setPurchased((prev) => new Set([...prev, propId]));
  };

  const game1Props = MOCK_PROPS.filter((p) => p.gameId === 'game-1');
  const game2Props = MOCK_PROPS.filter((p) => p.gameId === 'game-2');

  return (
    <div className="min-h-screen bg-surface text-cream">
      <div className="max-w-sm mx-auto px-3 py-8 space-y-6">
        <div>
          <h1 className="text-xl font-bold text-cream mb-1">Player Props Preview</h1>
          <p className="text-xs text-warm-gray/60">
            Collapsible player cards with inline category sliders.
            Expand a player to see their prop categories.
          </p>
        </div>

        {/* Active game */}
        <div>
          <h2 className="text-[10px] text-warm-gray/70 font-medium uppercase tracking-wider mb-3">Active Game</h2>
          <GamePlayerPropCard
            game={MOCK_GAMES[0]}
            props={game1Props}
            myProps={MOCK_MY_PROPS}
            purchasedPropIds={purchased}
            onPurchase={handlePurchase}
            disabled={false}
          />
        </div>

        {/* Game with a purchased/resolved prop */}
        <div>
          <h2 className="text-[10px] text-warm-gray/70 font-medium uppercase tracking-wider mb-3">Game with Purchased Prop (Filipowski Goals = HIT)</h2>
          <GamePlayerPropCard
            game={MOCK_GAMES[1]}
            props={game2Props}
            myProps={MOCK_MY_PROPS}
            purchasedPropIds={purchased}
            onPurchase={handlePurchase}
            disabled={false}
          />
        </div>

        {/* Locked state */}
        <div>
          <h2 className="text-[10px] text-warm-gray/70 font-medium uppercase tracking-wider mb-3">Locked Game</h2>
          <GamePlayerPropCard
            game={MOCK_GAMES[0]}
            props={game1Props}
            myProps={MOCK_MY_PROPS}
            purchasedPropIds={purchased}
            onPurchase={handlePurchase}
            disabled={true}
          />
        </div>

        {/* Purchase log */}
        {purchased.size > MOCK_MY_PROPS.length && (
          <div className="rounded-lg bg-surface-3 border border-surface-4 p-3">
            <p className="text-[10px] text-warm-gray/70 font-medium uppercase tracking-wider mb-2">Purchase Log</p>
            <div className="space-y-1">
              {[...purchased].filter((id) => !MOCK_MY_PROPS.some((m) => m.undercardPropId === id)).map((id) => {
                const prop = MOCK_PROPS.find((p) => p.id === id);
                return prop ? (
                  <div key={id} className="text-[11px] text-cream/70">
                    {prop.description} — {prop.costCoins}c @ {prop.bonusMultiplier}x
                  </div>
                ) : null;
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
